import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { sortByName } from '../../../../utils/sortingUtils';

const POPOVER_GAP = 8;
const VIEWPORT_PADDING = 8;
const POPOVER_WIDTH = 280;

function positionFromAnchor(anchor, popover) {
    const rect = anchor.getBoundingClientRect();
    const width = popover?.offsetWidth || Math.min(POPOVER_WIDTH, window.innerWidth * 0.7);
    const height = popover?.offsetHeight || 0;
    const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
    const left = Math.max(VIEWPORT_PADDING, Math.min(rect.right - width, maxLeft));
    const preferredTop = rect.bottom + POPOVER_GAP;
    const maxTop = window.innerHeight - height - VIEWPORT_PADDING;
    const top = height > 0
        ? Math.max(VIEWPORT_PADDING, Math.min(preferredTop, maxTop))
        : preferredTop;
    return { top, left };
}

function MethodRadio({ value, current, onChange, label }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input
                type="radio"
                name="ruleset-search-method"
                checked={current === value}
                onChange={() => onChange(value)}
            />
            {label}
        </label>
    );
}

/**
 * Specified search popover for filtering rule tables by name, features, values, or comments.
 */
export default function RulesetAdvancedSearch({
    searchMode,
    searchQuery,
    featureFilters,
    valueFilters,
    features,
    onApply,
    onClear,
    anchorRef,
    onClose
}) {
    const [draftMode, setDraftMode] = useState(searchMode === 'all' ? 'name' : searchMode);
    const [draftQuery, setDraftQuery] = useState(searchQuery);
    const [draftFeatures, setDraftFeatures] = useState(featureFilters);
    const [draftValues, setDraftValues] = useState(valueFilters);
    const [listSearch, setListSearch] = useState('');
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const popoverRef = useRef(null);

    const updatePosition = useCallback(() => {
        const anchor = anchorRef?.current;
        if (!anchor) return;
        setCoords(positionFromAnchor(anchor, popoverRef.current));
    }, [anchorRef]);

    useLayoutEffect(() => {
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [updatePosition, draftMode]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (anchorRef?.current?.contains(e.target)) return;
            if (popoverRef.current?.contains(e.target)) return;
            onClose?.();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [anchorRef, onClose]);

    const featureOptions = useMemo(() => {
        const seen = new Set();
        const unique = [];
        (features || []).forEach(f => {
            const name = f?.name;
            if (!name || seen.has(name.toLowerCase())) return;
            seen.add(name.toLowerCase());
            unique.push({ id: f.id || name, name });
        });
        return sortByName(unique, 'name');
    }, [features]);

    const valueOptions = useMemo(() => {
        const seen = new Set();
        const unique = [];
        (features || []).forEach(f => {
            (f.values || f.Values || []).forEach(v => {
                const value = v?.value ?? v?.Value;
                if (!value) return;
                const key = String(value).toLowerCase();
                if (seen.has(key)) return;
                seen.add(key);
                const displayText = v.displayText || v.DisplayText || value;
                unique.push({ id: v.id || v.Id || value, value, name: displayText });
            });
        });
        return sortByName(unique, 'name');
    }, [features]);

    const filteredFeatureOptions = useMemo(() => {
        const q = listSearch.trim().toLowerCase();
        if (!q) return featureOptions;
        return featureOptions.filter(f => f.name.toLowerCase().includes(q));
    }, [featureOptions, listSearch]);

    const filteredValueOptions = useMemo(() => {
        const q = listSearch.trim().toLowerCase();
        if (!q) return valueOptions;
        return valueOptions.filter(v =>
            v.name.toLowerCase().includes(q) || String(v.value).toLowerCase().includes(q)
        );
    }, [valueOptions, listSearch]);

    const toggleFeature = (name) => {
        setDraftFeatures(prev => (
            prev.includes(name)
                ? prev.filter(item => item !== name)
                : [...prev, name]
        ));
    };

    const toggleValue = (value) => {
        setDraftValues(prev => (
            prev.includes(value)
                ? prev.filter(item => item !== value)
                : [...prev, value]
        ));
    };

    const handleApply = () => {
        if (draftMode === 'features') {
            onApply({
                mode: draftFeatures.length > 0 ? 'features' : 'all',
                query: '',
                featureFilters: draftFeatures,
                valueFilters: []
            });
        } else if (draftMode === 'values') {
            onApply({
                mode: draftValues.length > 0 ? 'values' : 'all',
                query: '',
                featureFilters: [],
                valueFilters: draftValues
            });
        } else {
            const query = draftQuery.trim();
            const textMode = draftMode === 'comments' ? 'comments' : 'name';
            onApply({
                mode: query ? textMode : 'all',
                query: draftQuery,
                featureFilters: [],
                valueFilters: []
            });
        }
        onClose?.();
    };

    const handleClear = () => {
        onClear();
        onClose?.();
    };

    const renderCheckboxList = (options, selected, onToggle, emptyLabel, getCheckedValue) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <input
                className="input-field"
                placeholder={draftMode === 'values' ? 'Search values...' : 'Search features...'}
                value={listSearch}
                onChange={e => setListSearch(e.target.value)}
                style={{ width: '100%', margin: 0, padding: '0.4rem 0.5rem', fontSize: '0.8rem' }}
                autoFocus
            />
            <div style={{
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                background: 'var(--bg-secondary)'
            }}>
                {options.length === 0 ? (
                    <div style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        {emptyLabel}
                    </div>
                ) : (
                    options.map(opt => {
                        const checkedValue = getCheckedValue(opt);
                        return (
                            <label
                                key={opt.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.45rem',
                                    padding: '6px 8px',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(checkedValue)}
                                    onChange={() => onToggle(checkedValue)}
                                />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {opt.name}
                                </span>
                            </label>
                        );
                    })
                )}
            </div>
            {selected.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Match tables containing all {selected.length} selected
                </div>
            )}
        </div>
    );

    if (typeof document === 'undefined') return null;

    return createPortal(
                <div
                    ref={popoverRef}
                    className="select-dropdown fade-in"
                    style={{
                        position: 'fixed',
                        top: coords.top,
                        left: coords.left,
                        zIndex: 1000,
                        minWidth: '240px',
                        width: 'min(280px, 70vw)',
                        padding: '0.75rem',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.65rem'
                    }}
                >
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        Specified search
                    </div>

                    <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                            Method
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem' }}>
                            <MethodRadio value="name" current={draftMode} onChange={(mode) => { setDraftMode(mode); setListSearch(''); }} label="Table name" />
                            <MethodRadio value="features" current={draftMode} onChange={(mode) => { setDraftMode(mode); setListSearch(''); }} label="Features" />
                            <MethodRadio value="values" current={draftMode} onChange={(mode) => { setDraftMode(mode); setListSearch(''); }} label="Values" />
                            <MethodRadio value="comments" current={draftMode} onChange={(mode) => { setDraftMode(mode); setListSearch(''); }} label="Comments" />
                        </div>
                    </div>

                    {(draftMode === 'name' || draftMode === 'comments') && (
                        <input
                            className="input-field"
                            placeholder={draftMode === 'comments' ? 'Comment contains...' : 'Table name contains...'}
                            value={draftQuery}
                            onChange={e => setDraftQuery(e.target.value)}
                            style={{ width: '100%', margin: 0, padding: '0.4rem 0.5rem', fontSize: '0.8rem' }}
                            autoFocus
                        />
                    )}
                    {draftMode === 'features' && renderCheckboxList(
                        filteredFeatureOptions,
                        draftFeatures,
                        toggleFeature,
                        'No features match search',
                        opt => opt.name
                    )}
                    {draftMode === 'values' && renderCheckboxList(
                        filteredValueOptions,
                        draftValues,
                        toggleValue,
                        'No values match search',
                        opt => opt.value
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem' }}>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={handleClear}
                            style={{ margin: 0, padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            className="submit-btn"
                            onClick={handleApply}
                            style={{ margin: 0, padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                        >
                            Apply
                        </button>
                    </div>
                </div>,
                document.body
    );
}
